import React from 'react'
import { Link } from 'react-router-dom'
import {Clock, MapPin, ExternalLink, Star, Train, CreditCard, Users} from 'lucide-react'

function ShimokitazawaPage() {
  const topPicks = [
    {
      id: 1,
      type: 'Hotel',
      name: 'Shibuya Excel Hotel Tokyu',
      price: '¥12,000/night',
      rating: 4.5,
      link: '#'
    },
    {
      id: 2,
      type: 'Tour',
      name: 'Shimokita Walking Tour',
      price: '¥4,500',
      rating: 5.0,
      link: '#'
    },
    {
      id: 3,
      type: 'Experience',
      name: 'Vintage Shopping Pass',
      price: '¥2,000',
      rating: 4.8,
      link: '#'
    }
  ]

  return (
    <div className="pt-16 min-h-screen bg-[#0A0A0A]">
      {/* Hero Image */}
      <div className="relative h-[60vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1200&q=80"
          alt="Shimokitazawa street scene"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
        
        {/* Breadcrumb */}
        <div className="absolute top-8 left-0 right-0">
          <div className="max-w-5xl mx-auto px-4">
            <Link to="/" className="text-gray-400 hover:text-[#00E5FF] transition-colors">
              Home
            </Link>
            <span className="text-gray-600 mx-2">/</span>
            <Link to="/hipster-districts" className="text-gray-400 hover:text-[#00E5FF] transition-colors">
              Hipster Districts
            </Link>
          </div>
        </div>

        {/* Title */}
        <div className="absolute bottom-12 left-0 right-0">
          <div className="max-w-5xl mx-auto px-4">
            <span className="inline-block px-4 py-1 bg-[#00E5FF] text-[#0A0A0A] font-bold text-sm rounded-full mb-4">
              Hipster Districts
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Shimokitazawa: Tokyo's Coolest Vintage & Music District
            </h1>
            <div className="flex items-center gap-4 text-gray-400 text-sm">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                8 min read
              </span>
              <span>•</span>
              <span>Updated January 2024</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Layout */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <article className="lg:w-2/3">
            {/* Quick Summary Box */}
            <div className="bg-gradient-to-br from-[#00E5FF]/10 to-[#FF006E]/10 border-2 border-[#00E5FF]/30 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-[#00E5FF]">⚡</span>
                Quick Summary
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                Shimokitazawa (下北沢) is Tokyo's hippest neighborhood, packed with vintage clothing stores, indie music venues, cozy cafes, and underground theater. This guide shows you how to experience the best of "Shimokita" in one perfect day.
              </p>

              {/* Quick Info Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-[#0A0A0A]/50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <Clock className="w-4 h-4" />
                    Best Time
                  </div>
                  <div className="text-white font-semibold">Afternoon to Evening</div>
                </div>
                <div className="bg-[#0A0A0A]/50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <CreditCard className="w-4 h-4" />
                    Budget
                  </div>
                  <div className="text-white font-semibold">¥3,000-6,000</div>
                </div>
                <div className="bg-[#0A0A0A]/50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <Train className="w-4 h-4" />
                    Location
                  </div>
                  <div className="text-white font-semibold">15 min from Shinjuku</div>
                </div>
                <div className="bg-[#0A0A0A]/50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <Users className="w-4 h-4" />
                    Duration
                  </div>
                  <div className="text-white font-semibold">4-6 hours</div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://www.getyourguide.com/tokyo-walking-tours" target="_blank" rel="noopener noreferrer" className="flex-1 px-6 py-3 bg-gradient-to-r from-[#00E5FF] to-[#00B8CC] text-white font-bold rounded-full hover:shadow-lg hover:shadow-[#00E5FF]/50 transition-all flex items-center justify-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Book Walking Tour
                </a>
                <a href="https://www.google.com/maps/dir//Shimokitazawa+Station" target="_blank" rel="noopener noreferrer" className="flex-1 px-6 py-3 bg-gradient-to-r from-[#FF006E] to-[#CC0058] text-white font-bold rounded-full hover:shadow-lg hover:shadow-[#FF006E]/50 transition-all flex items-center justify-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Get Directions
                </a>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-invert prose-lg max-w-none">
              <div className="text-gray-300 space-y-6">
                <p className="text-xl leading-relaxed">
                  Shimokitazawa isn't just another Tokyo neighborhood—it's a vibe. Narrow streets lined with vintage shops, 
                  indie music venues hidden in basements, and cafes where you'll want to spend hours. This is where Tokyo's 
                  creative crowd comes to shop, hang out, and just be cool.
                </p>

                <h2 className="text-3xl font-bold text-white mt-12 mb-4">Getting There</h2>
                <p>
                  Shimokitazawa Station is on the Keio Inokashira Line (15 min from Shibuya) and Odakyu Line (7 min from Shinjuku). 
                  Take the South Exit for the main shopping streets. You'll know you're in the right place when you see vintage 
                  clothing stores everywhere.
                </p>

                <div className="bg-[#1A1A1A] rounded-xl p-6 my-6">
                  <h3 className="text-xl font-bold text-white mb-3">🚆 Transport Options</h3>
                  <ul className="space-y-2 text-gray-300 list-none pl-0">
                    <li>• From Shinjuku: Odakyu Line (7 min, ¥170)</li>
                    <li>• From Shibuya: Keio Inokashira Line (15 min, ¥170)</li>
                    <li>• JR Pass holders: Take JR to Shinjuku, then switch to Odakyu</li>
                  </ul>
                </div>

                <h2 className="text-3xl font-bold text-white mt-12 mb-4">The Perfect Shimokita Day</h2>
                
                <h3 className="text-2xl font-bold text-[#00E5FF] mt-8 mb-3">2:00 PM - Start with Vintage Shopping</h3>
                <p>
                  Hit the vintage shops first while you have energy. The best ones are along the narrow streets south of the station. 
                  Must-visits: <strong>Flamingo</strong> (multi-floor vintage heaven), <strong>New York Joe Exchange</strong> (American vintage), 
                  and <strong>Chicago</strong> (curated selection). Prices are reasonable (¥1,000-5,000 for most items), 
                  and the quality is excellent.
                </p>

                <div className="bg-[#1A1A1A] border-l-4 border-[#00E5FF] p-6 rounded-r-lg my-6">
                  <h4 className="text-xl font-bold text-white mb-2">🛍️ Shopping Pro Tips</h4>
                  <ul className="text-gray-300 space-y-2 list-none pl-0">
                    <li>• Bring cash—many small shops don't take cards</li>
                    <li>• Check for stains and damage before buying</li>
                    <li>• Negotiate at smaller stores (politely!)</li>
                    <li>• Hit the second floors—best deals are upstairs</li>
                  </ul>
                </div>

                <h3 className="text-2xl font-bold text-[#00E5FF] mt-8 mb-3">4:00 PM - Cafe Break</h3>
                <p>
                  Take a break at one of Shimokita's cozy cafes. <strong>Bear Pond Espresso</strong> is legendary (but tiny and often has a wait). 
                  For more space, try <strong>Great Lakes</strong> or <strong>Komazawa Cafe</strong>. Order slow-drip coffee (¥500-800) and watch the neighborhood go by.
                </p>

                <h3 className="text-2xl font-bold text-[#00E5FF] mt-8 mb-3">6:00 PM - Record Store Hunting</h3>
                <p>
                  Even if you don't have a record player, browsing Shimokita's vinyl shops is essential. 
                  <strong>Jet Set Records</strong> and <strong>Disk Union</strong> have incredible selections. Jazz, rock, Japanese city pop—it's all here. 
                  Prices range from ¥500 for used singles to ¥5,000+ for rare albums.
                </p>

                <h3 className="text-2xl font-bold text-[#00E5FF] mt-8 mb-3">7:30 PM - Dinner & Drinks</h3>
                <p>
                  End the day with yakitori (grilled chicken skewers) at one of the local izakayas. Budget ¥2,000-3,000 for dinner with drinks.
                  The whole neighborhood lights up at night, and the street vibe gets even better.
                </p>

                <div className="bg-[#1A1A1A] border-l-4 border-[#FF006E] p-6 rounded-r-lg my-8">
                  <h4 className="text-xl font-bold text-white mb-2">💡 Insider Tip</h4>
                  <p className="text-gray-300 mb-0">
                    Skip the weekends if possible. Shimokitazawa gets packed on Saturdays and Sundays. 
                    Weekday afternoons offer the best shopping experience with less crowds.
                  </p>
                </div>

                <h2 className="text-3xl font-bold text-white mt-12 mb-4">Budget Breakdown</h2>
                <div className="bg-[#1A1A1A] rounded-xl p-6">
                  <ul className="space-y-3 text-gray-300 list-none pl-0">
                    <li className="flex justify-between">
                      <span>• Transport (round trip from Shibuya)</span>
                      <strong className="text-[#00E5FF]">¥500</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>• Vintage Shopping</span>
                      <strong className="text-[#00E5FF]">¥2,000-3,000</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>• Food & Drinks</span>
                      <strong className="text-[#00E5FF]">¥2,000-3,000</strong>
                    </li>
                    <li className="flex justify-between border-t border-gray-700 pt-3 mt-3">
                      <span className="font-bold">Total</span>
                      <strong className="text-[#FF006E] text-lg">¥4,500-6,500</strong>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 bg-gradient-to-r from-[#1A1A1A] to-[#0A0A0A] p-8 rounded-2xl border-2 border-[#00E5FF]/30">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Explore Shimokitazawa?</h3>
              <p className="text-gray-300 mb-6">
                Book a guided walking tour or get your transportation pass sorted before you go.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://www.getyourguide.com/tokyo-walking-tours" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-gradient-to-r from-[#00E5FF] to-[#00B8CC] text-white font-bold rounded-full hover:scale-105 transition-transform text-center">
                  Book Walking Tour →
                </a>
                <a href="https://www.japanrailpass.net/" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-[#1A1A1A] border-2 border-[#00E5FF] text-[#00E5FF] font-bold rounded-full hover:bg-[#00E5FF] hover:text-[#0A0A0A] transition-all text-center">
                  Get Transport Pass →
                </a>
              </div>
            </div>
          </article>

          {/* Sidebar - Top 3 Picks */}
          <aside className="lg:w-1/3">
            <div className="sticky top-24">
              <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] rounded-2xl p-6 border-2 border-[#FF006E]/30">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <Star className="w-6 h-6 text-[#FF006E]" fill="#FF006E" />
                  Top 3 Picks
                </h3>
                
                <div className="space-y-4">
                  {topPicks.map((pick) => (
                    <a key={pick.id} href={pick.link} className="block bg-[#0A0A0A]/50 rounded-xl p-4 border border-[#FF006E]/20 hover:border-[#FF006E] transition-all">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="text-xs text-gray-500 uppercase font-bold mb-1">{pick.type}</div>
                          <h4 className="text-white font-semibold text-sm">{pick.name}</h4>
                        </div>
                        <div className="flex items-center gap-1 text-yellow-400 text-sm">
                          <Star className="w-3 h-3" fill="currentColor" />
                          {pick.rating}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#FF006E] font-bold text-sm">{pick.price}</span>
                        <span className="text-[#00E5FF] hover:text-white transition-colors text-xs font-semibold">
                          View Deal →
                        </span>
                      </div>
                    </a>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-700">
                  <p className="text-gray-400 text-xs mb-3">
                    💰 Affiliate Disclosure: We earn from qualifying bookings at no extra cost to you.
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

export default ShimokitazawaPage